<script setup lang="ts">
import Modal from "@/ui/components/modal.vue";
import { ref, toRaw, watchEffect } from "vue";
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import { titleInputRules } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";

const loading = ref(false);

const client = useApiClient("bookingExperts");
const { curve } = defineProps<{ curve: PriceModelCurveDO }>();
const copy = ref<PriceModelCurveDO>();
watchEffect(() => {
	copy.value = structuredClone(toRaw(curve));
});

const visible = defineModel<boolean>("visible");
const allowSubmit = defineModel<boolean>("allowSubmit", {
	default: true,
});

async function cloneCurve() {
	loading.value = true;
	const response = await toastApiResult(
		client.createPriceModelCurve(copy.value)
	);

	if (response.isOk()) {
		emit("duplicated", response.value);
	}

	visible.value = loading.value = false;
}

const emit = defineEmits<{
	(e: "duplicated", curve: PriceModelCurveDO): void;
}>();
</script>

<template>
	<Modal
		v-model:visible="visible"
		v-model:allow-submit="allowSubmit"
		:title="`Duplicating: ${curve.name}`"
		icon="fa-solid fa-chart-line"
		width="35rem"
	>
		<v-form v-model="allowSubmit">
			<v-menu :close-on-content-click="false" location="bottom">
				<template v-slot:activator="{ props }">
					<v-btn
						elevation="0"
						v-bind="props"
						variant="text"
						size="30"
						max-width="30"
						:disabled="loading"
						class="flex-grow-1 justify-center border"
					>
						<span
							class="chit"
							:style="`background: ${copy.color}; margin: 0;`"
						/>
					</v-btn>
					<span class="text-medium-emphasis text-caption mb-2">
						Choose a distinctive color to help identify this curve in calendars
						and reports.
					</span>
				</template>

				<v-card max-width="300" height="230">
					<v-color-picker
						v-model="copy.color"
						elevation="0"
						hide-canvas
						hide-sliders
						hide-inputs
						show-swatches
						swatches-max-height="230"
						:modes="['hex']"
						border
						width="100%"
					/>
				</v-card>
			</v-menu>

			<v-text-field
				v-model="copy.name"
				label="Curve Name"
				placeholder="e.g., Summer Season Model"
				variant="outlined"
				density="comfortable"
				:disabled="loading"
				class="mt-2"
				:rules="titleInputRules(t)"
			/>

			<!-- Description field -->
			<v-textarea
				v-model="copy.description"
				:disabled="loading"
				label="Description"
				placeholder="Describe the purpose and behavior of this curve..."
				variant="outlined"
				density="comfortable"
				rows="3"
				class="mt-2"
				hide-details
				auto-grow
			/>
		</v-form>

		<template #actions>
			<v-btn :disabled="loading" color="red" @click="visible = false">
				Cancel
			</v-btn>
			<v-btn
				:loading="loading"
				:disabled="allowSubmit === false"
				variant="tonal"
				@click="cloneCurve"
			>
				Duplicate
			</v-btn>
		</template>
	</Modal>
</template>

<style scoped lang="scss">
:deep(.v-list-item-subtitle) {
	max-width: 126px;
	overflow: hidden;
	text-overflow: ellipsis;
}

.item {
	display: flex;
	gap: 0.25rem;
	align-items: center;

	& .chit {
		background: #f8f8f8;
		border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
		border-radius: 10px;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5),
			inset 0 -1px 2px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
		height: 15px;
		width: 15px;
	}
}
</style>
